import { zodResolver } from '@hookform/resolvers/zod';
import { IntegrationAuthType } from 'modules/mobileApps/types';
import { FC, PropsWithChildren } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { useIntegrationMetadataContext } from 'modules/mobileApps/context/integrationMetadata';
import { FirebaseNotificationType, IntegrationFormField } from './types';

const BaseSchema = z.object({});

const schema = z.discriminatedUnion(IntegrationFormField.authType, [
  BaseSchema.extend({ [IntegrationFormField.authType]: z.undefined() }),
  BaseSchema.extend({
    [IntegrationFormField.authType]: z.literal(IntegrationAuthType.FIREBASE_JSON),
    [IntegrationFormField.firebaseJsonFile]: z
      .custom<File | undefined>()
      .refine((file) => !!file, 'JSON file required to save existing integration'),
    [IntegrationFormField.firebaseNotificationType]: z.custom<FirebaseNotificationType>(),
  }),
  BaseSchema.extend({
    [IntegrationFormField.authType]: z.literal(IntegrationAuthType.FIREBASE),
    [IntegrationFormField.privateKey]: z.string().min(1, 'API Key required to add credential'),
    [IntegrationFormField.firebaseNotificationType]: z.custom<FirebaseNotificationType>(),
  }),
  BaseSchema.extend({
    [IntegrationFormField.authType]: z.literal(IntegrationAuthType.P8),
    [IntegrationFormField.p8File]: z
      .custom<File | undefined>()
      .refine((file) => !!file, '.p8 file required to save integration'),
    [IntegrationFormField.keyID]: z.string().min(1, 'Key ID required'),
    [IntegrationFormField.teamID]: z.string().min(1, 'Team ID required'),
    [IntegrationFormField.bundleID]: z.string().min(1, 'Bundle ID required'),
  }),
  BaseSchema.extend({
    [IntegrationFormField.authType]: z.literal(IntegrationAuthType.P12),
    [IntegrationFormField.p12file]: z
      .custom<File | undefined>()
      .refine((file) => !!file, '.p12 file required to save integration'),
    [IntegrationFormField.p12password]: z.string().optional(),
  }),
  BaseSchema.extend({
    [IntegrationFormField.authType]: z.literal(IntegrationAuthType.PEM),
    [IntegrationFormField.certificate]: z
      .string()
      .min(1, 'Push certificate required to add credential'),
    [IntegrationFormField.privateKey]: z
      .string()
      .min(1, 'RSA private key required to add credential'),
  }),
]);

export type IntegrationFormValues = z.infer<typeof schema>;

export const INTEGRATION_FORM_DEFAULT_VALUES: Record<IntegrationFormField, any> = {
  [IntegrationFormField.authType]: undefined,
  [IntegrationFormField.bundleID]: '',
  [IntegrationFormField.certificate]: '',
  [IntegrationFormField.firebaseJsonFile]: undefined,
  [IntegrationFormField.firebaseNotificationType]: FirebaseNotificationType.NOTIFICATION,
  [IntegrationFormField.keyID]: '',
  [IntegrationFormField.p12file]: undefined,
  [IntegrationFormField.p12password]: '',
  [IntegrationFormField.p8File]: undefined,
  [IntegrationFormField.privateKey]: '',
  [IntegrationFormField.teamID]: '',
};

export const IntegrationFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const { selectedIntegration } = useIntegrationMetadataContext();

  const defaultValues = {
    ...INTEGRATION_FORM_DEFAULT_VALUES,
    ...(selectedIntegration?.androidUseOsNotifications === false
      ? { firebaseNotificationType: FirebaseNotificationType.DATA }
      : {}),
  };

  const methods = useForm<IntegrationFormValues>({
    defaultValues,
    mode: 'onChange',
    shouldUnregister: true,
    resolver: zodResolver(schema),
  });

  return <FormProvider {...methods}>{children}</FormProvider>;
};

export const useIntegrationFormContext = () => useFormContext<IntegrationFormValues>();
